import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MiqBehaviors } from '@shared/behaviors';
import { toFloat, toInt } from '@shared/numerics/parse-number';
import { formatNumber, isMasked } from '@shared/utils/helpers/sanitizers';
import { TextInput } from '../text-input';

const restrictedKeyCodesByType = {
  numeric: [],
  integer: [110, 190],
};

@Component({
  selector: 'miq-number',
  templateUrl: './number.component.html',
  styleUrls: ['./number.component.scss'],
  providers: [{ provide: MiqBehaviors, useExisting: NumberComponent }],
  standalone: false,
})
export class NumberComponent extends TextInput implements OnInit, OnChanges {
  @Input() placeholder = '';
  @Input() format = true;
  @Input() temp: string;
  @Input() precision: string;
  @Input() autocomplete: boolean;
  @Input() dataType = 'numeric';
  @Input() allowNegatives = false;

  @Output() currentValueChange = new EventEmitter<any>();

  declare public fc: UntypedFormControl;
  public valueToDisplay: string;
  public autocompleteValue = 'no-autofill';
  public restrictedKeyCodes = [];
  public allowedKeyCodes = [];

  protected decimalsCount = 0;
  public step: number;

  ngOnInit(): void {
    super.ngOnInit();
    this.autocompleteValue = this.autocomplete ? 'on' : 'no-autofill';
  }

  protected controlValues() {
    if (this.currentValue) {
      this.setDecimalCount(this.currentValue);
    }
    this.valueToDisplay = this.formatValueToDisplay(this.currentValue);
    return { value: this.valueToDisplay, disabled: this.disabled || this.isMaskedValue };
  }

  public controlValidators() {
    // super.controlValidators evals required validator
    const validators = super.controlValidators();
    if (this.column) {
      if (this.column.min != null) {
        validators.push(Validators.min(this.column.min));
      }
      if (this.column.max != null) {
        validators.push(Validators.max(this.column.max));
      }

      if (this.column.step != null) {
        this.step = this.column.step;
      }
    }
    return validators;
  }

  public ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
    const { currentValue, dataType, allowNegatives } = changes;
    if (!!currentValue && !currentValue.firstChange) {
      this.setDecimalCount(this.currentValue);
      this.valueToDisplay = this.formatValueToDisplay(this.currentValue);
      this.fc.setValue(this.valueToDisplay);
    }
    if (dataType) {
      this.restrictedKeyCodes = restrictedKeyCodesByType[this.dataType] || [];
    }
    if (allowNegatives && allowNegatives.firstChange === true) {
      this.allowedKeyCodes = [189];
    }
  }

  public emitAndFormat() {
    if (this.isValidToFormat(this.fc.value)) {
      this.setDecimalCount(this.fc.value);
      this.valueToDisplay = this.formatValueToDisplay(this.fc.value);
      const value =
        this.decimalsCount > 0 && this.dataType === 'numeric' ? toFloat(this.fc.value) : toInt(this.fc.value);
      this.fc.setValue(this.valueToDisplay);
      return this.currentValueChange.emit(this.valueSetter ? this.valueSetter(value) : value);
    }
    this.currentValueChange.emit(null);
    this.decimalsCount = null;
  }
  formatValueToDisplay(value) {
    const validToFormat = this.isValidToFormat(value);
    if (isMasked(value)) {
      return value;
    }
    if (this.format && validToFormat) {
      return `${this.applyFormat(value)}`;
    } else if (validToFormat) {
      // If value can be formatted, check if value is not numeric
      return !(this.dataType !== 'numeric') ? Number.parseInt(value) : value; // Else if value is numeric, return value as is
    }
    return ''; // Else if value can't be formatted, return empty string
  }
  public onPaste(event: ClipboardEvent) {
    if (!this.fc.disabled) {
      const clipboardData = event.clipboardData?.getData('text');
      if (!this.isDataPasteable(clipboardData)) {
        return;
      }
      const newValue = this.getNewValueFromPaste(event.target as HTMLInputElement, clipboardData);
      this.fc.setValue(newValue);
      this.emitAndFormat();
    }
  }

  public isDataPasteable(data: string) {
    if (typeof data !== 'string') return false;
    return this.dataType === 'numeric'
      ? !!data.match(/^(([0-9]+,)*(,[0-9]+)*(,[0-9]+,)*)((\.)?[0-9])*(([0-9]+\.|\.[0-9]+|[0-9]*)$)/)
      : !!data.match(/^(([0-9]+,)*(,[0-9]+)*(,[0-9]+,)*)([0-9]*$)/);
  }

  public getNewValueFromPaste(target: HTMLInputElement, pastedValue: string) {
    const startIndex = target.selectionStart ?? 0;
    const endIndex = target.selectionEnd ?? 0;
    const currentValue: string = target['value'];
    return `${currentValue.substring(0, startIndex)}${pastedValue}${currentValue.substring(endIndex)}`.replace(',', '');
  }

  protected applyFormat(val: number | string): string {
    const digitInfo = this.setDigitInfo();
    const parsedVal = this.decimalsCount > 0 ? toFloat(val) : toInt(val);
    return formatNumber(parsedVal, 'en', digitInfo);
  }
  private setDigitInfo() {
    if (!!this.precision) {
      return this.precision;
    }
    return this.decimalsCount ? `0.${this.decimalsCount}` : '0.0';
  }

  protected isValidToFormat(value) {
    return value != null && !Number.isNaN(Number.parseFloat(value));
  }

  private setDecimalCount(value: string | number) {
    const [, decimals] = String(value).split('.');
    this.decimalsCount = decimals != null ? decimals.length : 0;
  }
}
